import React, { useRef, useState } from 'react';
import Modal from '@material-ui/core/Modal';
import { SecurityPlus } from 'types/shared/documents/restaurants';
import { validHolders } from './securityRights';
import { PRODUCT_OVERLAY_PROPS } from './overlay';

type Props = {
  productLabel: string;
  settings: SecurityPlus;
  holders: string[];
  /** null turns the PIN protection off. */
  onApply: (holders: string[] | null) => void;
  onCancel: () => void;
  /** Absent while the editor has unsaved changes. */
  onManageSignatures?: () => void;
};

const ProtectionPanel = ({
  productLabel,
  settings,
  holders,
  onApply,
  onCancel,
  onManageSignatures,
}: Props) => {
  const signatures = Object.keys(settings.signatures ?? {});
  const groups = settings.rightGroups ?? [];
  const allHolders = validHolders(settings, [
    ...signatures,
    ...groups.map((group) => group.id),
  ]);

  const [enabled, setEnabled] = useState(holders.length > 0);
  const [selected, setSelected] = useState<string[]>(
    holders.length ? holders : allHolders,
  );
  const chosen = validHolders(settings, selected);
  const firstControl = useRef<HTMLInputElement>(null);

  const toggle = (holder: string) =>
    setSelected((current) =>
      current.includes(holder)
        ? current.filter((x) => x !== holder)
        : [...current, holder],
    );

  return (
    <Modal
      {...PRODUCT_OVERLAY_PROPS}
      open
      onClose={onCancel}
      onRendered={() => firstControl.current?.focus()}
    >
      <div
        className="product-editor-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-protection-title"
        tabIndex={-1}
      >
        <h3 id="product-protection-title">Zugriffsschutz: {productLabel}</h3>
        <p className="product-editor-panel-text">
          Mitarbeiter mit einer ausgewählten Signatur öffnen {productLabel}{' '}
          nach Eingabe ihrer PIN.
        </p>
        <label className="product-editor-switch">
          <input
            ref={firstControl}
            type="checkbox"
            role="switch"
            aria-checked={enabled}
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          PIN-Schutz aktiv
        </label>

        {enabled && (
          <>
            <fieldset>
              <legend>Signaturen</legend>
              {!signatures.length && (
                <p className="product-editor-panel-text">
                  Es gibt noch keine Signaturen.
                </p>
              )}
              {signatures.map((signature) => (
                <label key={signature} className="product-editor-option">
                  <input
                    type="checkbox"
                    checked={selected.includes(signature)}
                    onChange={() => toggle(signature)}
                  />
                  {signature}
                </label>
              ))}
            </fieldset>
            {!!groups.length && (
              <fieldset>
                <legend>Gruppen</legend>
                {groups.map((group) => {
                  const usable = allHolders.includes(group.id);
                  return (
                    <label key={group.id} className="product-editor-option">
                      <input
                        type="checkbox"
                        disabled={!usable}
                        checked={usable && selected.includes(group.id)}
                        onChange={() => toggle(group.id)}
                      />
                      {group.name}
                      {!usable && ' (keine Signatur)'}
                    </label>
                  );
                })}
              </fieldset>
            )}
            {!chosen.length && (
              <p className="product-editor-panel-warning" role="alert">
                Wähle mindestens eine Signatur. Ohne Signatur öffnet sich{' '}
                {productLabel} ohne PIN.
              </p>
            )}
          </>
        )}

        <p className="product-editor-panel-text">
          Signaturen und Gruppen verwaltest du in den Einstellungen unter
          Security Plus.{' '}
          {onManageSignatures ? (
            <button
              type="button"
              className="product-editor-link"
              onClick={onManageSignatures}
            >
              Zu den Einstellungen
            </button>
          ) : (
            'Speichere zuerst deine Änderungen.'
          )}
        </p>

        <div className="product-editor-panel-actions">
          <button
            type="button"
            className="product-editor-button"
            onClick={onCancel}
          >
            Abbrechen
          </button>
          <button
            type="button"
            className="product-editor-button is-primary"
            disabled={enabled && !chosen.length}
            onClick={() => onApply(enabled ? chosen : null)}
          >
            Übernehmen
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ProtectionPanel;
